import styled from '@emotion/styled'
import {
    faBars,
    faCalendarDays,
    faEnvelope,
    faExternalLink,
    faFileSignature,
    faHomeLg,
    faKey,
    faQuestionCircle,
    faTimes,
    faTrophy,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Breakpoint from '../../../Breakpoint'
import { useMenu } from '../../../context/MenuContext'
import { useClickOutside } from '../../../hooks/useClickOutside'
import { isTokenExpired } from '../../../services/jwt.service'
import { ExternalLink } from '../ExternalLink'
import MainLink from '../MainLink'
import { SideBar, SidebarLink, SidebarLinkExternal } from '../user-interface/MemberSideBar'

export const MainHeaderNav = ({ selectedLngLowerCase }: { selectedLngLowerCase: string }) => {
    const tokenMember = localStorage.getItem('tokenMember')?.split('Bearer ')[1] ?? ''
    let tokenExpired
    if (tokenMember) tokenExpired = isTokenExpired(tokenMember)
    const { closeMenu } = useMenu()

    const [isShowMenuMobile, setIsShowMenuMobile] = useState(false)

    if (tokenExpired) localStorage.removeItem('tokenMember')

    const sidebarRef = useRef<HTMLDivElement>(null)

    useClickOutside(sidebarRef, closeMenu)

    const { t } = useTranslation('translation')

    return (
        <NavContainer>
            <MainNav>
                <MainLink
                    to={`/${selectedLngLowerCase}/`}
                    className={location.pathname === `/${selectedLngLowerCase}/` ? 'active' : ''}
                >
                    <FontAwesomeIcon icon={faHomeLg} /> {t('header.menu.home')}
                </MainLink>
                <MainLink
                    to={`/${selectedLngLowerCase}/horaires-et-tarifs`}
                    className={location.pathname === `/${selectedLngLowerCase}/horaires-et-tarifs` ? 'active' : ''}
                >
                    {t('header.menu.planning')}
                </MainLink>
                <ExternalLink href="https://adherer.ffbad.club/BVA59" target="_blank" rel="noreferrer">
                    {t('header.menu.registration')} <FontAwesomeIcon icon={faExternalLink} />
                </ExternalLink>
                <MainLink
                    to={`/${selectedLngLowerCase}/competitions`}
                    className={location.pathname === `/${selectedLngLowerCase}/competitions` ? 'active' : ''}
                >
                    {t('header.menu.competition')}
                </MainLink>
                <MainLink
                    to={`/${selectedLngLowerCase}/faq`}
                    className={location.pathname === `/${selectedLngLowerCase}/faq` ? 'active' : ''}
                >
                    {t('header.menu.faq')}
                </MainLink>
                <MainLink
                    to={`/${selectedLngLowerCase}/contact`}
                    className={location.pathname === `/${selectedLngLowerCase}/contact` ? 'active' : ''}
                >
                    {t('header.menu.contact')}
                </MainLink>
                <MainLink
                    to={`/${selectedLngLowerCase}/login`}
                    className={location.pathname === `/${selectedLngLowerCase}/login` ? 'active' : ''}
                >
                    {t('header.menu.login')}
                </MainLink>
            </MainNav>
            <IconBurger onClick={() => setIsShowMenuMobile(true)}>
                <FontAwesomeIcon icon={faBars} />
            </IconBurger>

            {isShowMenuMobile && (
                <SideBar ref={sidebarRef} isOpen={isShowMenuMobile}>
                    <CloseButton onClick={() => setIsShowMenuMobile(false)}>
                        <FontAwesomeIcon icon={faTimes} />
                    </CloseButton>
                    <SidebarLink
                        to={`/${selectedLngLowerCase}/`}
                        onClick={() => setIsShowMenuMobile(false)}
                        className={location.pathname === `/${selectedLngLowerCase}/` ? 'active' : ''}
                    >
                        <FontAwesomeIcon icon={faHomeLg} /> {t('header.menu.home')}
                    </SidebarLink>
                    <SidebarLink
                        to={`/${selectedLngLowerCase}/horaires-et-tarifs`}
                        className={location.pathname === `/${selectedLngLowerCase}/horaires-et-tarifs` ? 'active' : ''}
                        onClick={() => setIsShowMenuMobile(false)}
                    >
                        <FontAwesomeIcon icon={faCalendarDays} /> {t('header.menu.planning')}
                    </SidebarLink>
                    <SidebarLinkExternal href="https://adherer.ffbad.club/BVA59" target="_blank" rel="noreferrer">
                        <FontAwesomeIcon icon={faFileSignature} /> {t('header.menu.registration')}{' '}
                        <FontAwesomeIcon icon={faExternalLink} />
                    </SidebarLinkExternal>
                    <SidebarLink
                        to={`/${selectedLngLowerCase}/competitions`}
                        className={location.pathname === `/${selectedLngLowerCase}/competitions` ? 'active' : ''}
                        onClick={() => setIsShowMenuMobile(false)}
                    >
                        <FontAwesomeIcon icon={faTrophy} /> {t('header.menu.competition')}
                    </SidebarLink>
                    <SidebarLink
                        to={`/${selectedLngLowerCase}/faq`}
                        className={location.pathname === `/${selectedLngLowerCase}/faq` ? 'active' : ''}
                        onClick={() => setIsShowMenuMobile(false)}
                    >
                        <FontAwesomeIcon icon={faQuestionCircle} /> {t('header.menu.faq')}
                    </SidebarLink>
                    <SidebarLink
                        to={`/${selectedLngLowerCase}/contact`}
                        className={location.pathname === `/${selectedLngLowerCase}/contact` ? 'active' : ''}
                        onClick={() => setIsShowMenuMobile(false)}
                    >
                        <FontAwesomeIcon icon={faEnvelope} /> {t('header.menu.contact')}
                    </SidebarLink>
                    <SidebarLink
                        to={`/${selectedLngLowerCase}/login`}
                        className={location.pathname === `/${selectedLngLowerCase}/login` ? 'active' : ''}
                        onClick={() => setIsShowMenuMobile(false)}
                    >
                        <FontAwesomeIcon icon={faKey} /> {t('header.menu.login')}
                    </SidebarLink>
                </SideBar>
            )}
        </NavContainer>
    )
}

const NavContainer = styled.div`
    display: flex;
    width: 80%;
    box-sizing: border-box;

    @media (max-width: ${Breakpoint.TABLET_L}) {
        width: 16%;
        justify-content: flex-end;
        width: min-content;
    }
`

const IconBurger = styled.div`
    display: none;

    & svg {
        width: 3rem;
        height: 3rem;
    }

    @media (max-width: ${Breakpoint.TABLET_L}) {
        display: flex;
        align-items: center;
        margin-right: 8%;
        cursor: pointer;
        font-size: 4rem;
        color: var(--primary-color);
    }
`

const CloseButton = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    color: var(--primary-color);
    font-size: 1.5rem;
    cursor: pointer;
    z-index: 1001;
    padding: 5px;

    & svg {
        width: 3rem;
        height: 3rem;
    }
`

const MainNav = styled.nav`
    display: flex;
    margin-right: 8%;
    align-items: center;
    font-weight: 500;
    justify-content: space-around;
    width: 100%;

    @media (max-width: ${Breakpoint.TABLET_L}) {
        display: none;
    }
`
